import React, { useState, useEffect } from 'react';
import { APP_VERSION } from '../version';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { hedefUnvan } from '../utils/hedef';
import './Login.css';

export default function Login() {
  const { login } = useAuth();
  const { t, lang, switchLang } = useLanguage();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  useEffect(() => {
    const err = params.get('error');
    if (err) setError(decodeURIComponent(err));
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(form.username, form.password);
      navigate(hedefUnvan());
    } catch (err) {
      setError(err.response?.data?.message || t('login.errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoft = () => {
    window.location.href = '/api/auth/microsoft';
  };

  return (
    <div className="login-page-v2">
      <div className="login-bg-orbs">
        <span className="orb orb-1" />
        <span className="orb orb-2" />
        <span className="orb orb-3" />
      </div>

      <div className="login-shell">
        <div className="login-brand">
          <div className="login-logo-wrap">
            <img src="/mtk-logo.svg" alt="EMS" />
          </div>
          <h1 className="login-brand-title">{t('login.schoolName')}</h1>
          <p className="login-brand-sub">{t('login.schoolName2')}</p>
        </div>

        <div className="login-card-v2">
          <div className="login-lang-switch">
            <button className={lang === 'az' ? 'active' : ''} onClick={() => switchLang('az')}>AZ</button>
            <button className={lang === 'en' ? 'active' : ''} onClick={() => switchLang('en')}>EN</button>
          </div>

          <div className="login-card-head">
            <h2>{t('login.title')}</h2>
            <p>{t('login.subtitle')}</p>
          </div>

          {error && (
            <div className="login-error-v2">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form-v2">
            <label className="login-input-wrap">
              <span className="login-input-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
              </span>
              <input
                type="text"
                placeholder={t('login.username')}
                value={form.username}
                onChange={e => setForm({ ...form, username: e.target.value })}
                required
                autoComplete="username"
              />
            </label>

            <label className="login-input-wrap">
              <span className="login-input-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
                </svg>
              </span>
              <input
                type={showPwd ? 'text' : 'password'}
                placeholder={t('login.password')}
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                required
                autoComplete="current-password"
              />
              <button type="button" className="login-pwd-toggle" onClick={() => setShowPwd(s => !s)} tabIndex={-1}>
                {showPwd ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </label>

            <button type="submit" className="login-submit-v2" disabled={loading}>
              {loading ? (
                <>
                  <span className="login-spinner" />
                  <span>{t('login.loading')}</span>
                </>
              ) : (
                <>
                  <span>{t('login.submit')}</span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                  </svg>
                </>
              )}
            </button>
          </form>

          <div className="login-divider-v2"><span>{t('login.or')}</span></div>

          <button className="login-ms-v2" onClick={handleMicrosoft} type="button">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 21 21" width="18" height="18">
              <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
              <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
              <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
            </svg>
            <span>{t('login.microsoft')}</span>
          </button>
        </div>

        <p className="login-footer">
          © Heydər Əliyev MTK · EMS v{APP_VERSION}
          <span className="login-author">Hazırlayan: Eldar Cəbrayılov</span>
        </p>
      </div>
    </div>
  );
}
